import { useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useModuleEnabled } from "../access/useFeatureAccess";
import { useAuthContext } from "../auth/AuthContext";
import { getPpToken, resolveIdentityEmail } from "../auth/ppToken";
import { featureFlags } from "../config/featureFlags";
import { resolveWorklistIdentity } from "../worklist/resolveWorklistIdentity";
import { useWorklistAccessGate } from "../worklist/useWorklistAccessGate";
import { buildWorklistMemberNavState } from "../worklist/worklistNavState";
import {
  PatientPriorityIndexProvider,
  UrgentPatientsCard,
  UrgentPatientsCardSkeleton,
  usePriorityPatients,
  type PriorityPatientMember,
} from "@astrana/ui/modules";

/**
 * Home-page summary widget for the Patient Priority Index — "Urgent and High
 * Risk Patients". Prints the provider's Urgent / High risk bucket counts and
 * the top urgent rows, then hands off to the full `/worklist` surface.
 *
 * WHY A CONTAINER AND NOT A DIRECT MOUNT. `UrgentPatientsCard` (@astrana/ui)
 * is deliberately presentational: it takes `members: PriorityPatientMember[]`
 * and derives every count and rail itself, and it fetches nothing. The panel
 * data therefore has to come from the same place `/worklist` gets it —
 * `usePriorityPatients`, which reads the module-scoped RTK Query store — so
 * this file supplies `<PatientPriorityIndexProvider>` (the store + identity
 * context) around a child that calls the hook. Inventing a second fetch path
 * against `ProviderOverview/PatientPriorityIndex` would let Home and the
 * worklist disagree about the same panel.
 *
 * ACCESS. Identical preconditions to WorklistPage: the VITE feature flag, the
 * `PriorityWorklist` persona module, and `useWorklistAccessGate`'s
 * PP-bearer + IPA + NPI resolution. THIS WIDGET still renders no gate, no
 * spinner and no error — anything short of `"ready"` renders nothing, and
 * the surface that OWNS the worklist (WorklistPage) keeps the whole gate
 * ladder. Home surfaces exactly ONE of those states, and not from here:
 * `HomePpConnectPrompt` renders a sign-in card for `"no-pp-session"`,
 * because a provider who has never linked a PP account would otherwise get a
 * blank landing page with no way to fix it. Everything else — an IPA fetch
 * failure, a missing NPI — stays silent: a landing page must not explain
 * those for a card the provider did not ask for.
 *
 * Once the gate IS ready, the widget shows `UrgentPatientsCardSkeleton`
 * while the panel pages in rather than leaving an empty slot — see the
 * `isComplete` gate below for why a partial panel must never be rendered as
 * the real card.
 */
export default function UrgentPatientsWidget() {
  const worklistEnabled = useModuleEnabled("PriorityWorklist");
  if (!featureFlags.patientPriorityIndex || !worklistEnabled) return null;
  return <UrgentPatientsWidgetGated />;
}

function UrgentPatientsWidgetGated() {
  const { selectedProvider, accessToken, accountLabel } = useAuthContext();
  const accessGate = useWorklistAccessGate();

  const identityEmail = resolveIdentityEmail(accountLabel);

  // Stable ref so `getAccessToken` always reads the LATEST auth state without
  // changing its own identity (and therefore the module-scoped baseQuery
  // closure) on every token refresh. Same pattern as WorklistPage.tsx.
  const authRef = useRef({ selectedProvider, accessToken, identityEmail });
  authRef.current = { selectedProvider, accessToken, identityEmail };

  const getAccessToken = useCallback((): string => {
    const { selectedProvider: sp, accessToken: at, identityEmail: email } =
      authRef.current;
    return getPpToken(sp, at, email) ?? "";
  }, []);

  const companyId = accessGate.resolvedIpaId ?? "";

  // userId/userRole MUST be decoded off the PP bearer `getAccessToken` returns,
  // not the primary Entra/Pathways session token — see WorklistPage.tsx's own
  // note on the diverging `sub` claims.
  const ppBearerToken = getPpToken(selectedProvider, accessToken, identityEmail);
  const { userId: nativeUserId, userRole: nativeUserRole } =
    resolveWorklistIdentity(ppBearerToken);

  if (accessGate.status !== "ready") return null;

  return (
    <PatientPriorityIndexProvider
      ppApiBaseUrl={`${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`}
      companyId={companyId}
      getAccessToken={getAccessToken}
      // `status === "ready"` guarantees a Luhn-validated 10-digit NPI.
      pcpNpi={accessGate.npi ?? ""}
      ipaId={companyId}
      userId={nativeUserId ?? ""}
      userRole={nativeUserRole}
      userEmail={identityEmail}
    >
      <UrgentPatientsPanel npi={accessGate.npi ?? ""} ipaId={companyId} />
    </PatientPriorityIndexProvider>
  );
}

function UrgentPatientsPanel({ npi, ipaId }: { npi: string; ipaId: string }) {
  const navigate = useNavigate();

  const pcpNpi = useMemo(() => (npi ? [npi] : []), [npi]);
  const ipaIds = useMemo(() => (ipaId ? [ipaId] : []), [ipaId]);

  // Same year / lookback / gap arguments the worklist widgets pass (see
  // RiskEvidencePanelWidget's "copied so the two surfaces resolve to the same
  // cohort" note). `includeGaps` is load-bearing, not an optimisation to trim:
  // `classifyPriority` reads open care gaps, so a gap-less panel would bucket
  // members differently here than on /worklist.
  const currentYear = new Date().getFullYear();
  const { items: members, isComplete, isError } = usePriorityPatients({
    pcpNpi,
    ipaId: ipaIds,
    serviceYear: currentYear,
    measureYear: currentYear,
    adtLookbackDays: 30,
    includeGaps: true,
    skip: pcpNpi.length === 0 || ipaIds.length === 0,
  });

  // Row click → the member's profile, via the SAME astrana: fallback route and
  // nav-state shape WorklistPage uses (see its `handleSelectMember` for why
  // name/dob/tabTitle have to be threaded through: these members skip the
  // profile's own name-resolving query, and Create Auth's PP crosswalk is
  // keyed on name + DOB + IPA). `astranaId` is always present on the row.
  const handleSelectPatient = useCallback(
    (member: PriorityPatientMember) => {
      const astranaId = String(member.astranaId);
      navigate(`/members/astrana:${encodeURIComponent(astranaId)}`, {
        state: buildWorklistMemberNavState(astranaId, {
          companyId: member.ipaId || ipaId,
          memberId: member.healthPlanMemberId,
          membKeyId: member.memberKeyId,
          name:
            member.lastName || member.firstName
              ? `${member.lastName}, ${member.firstName}`
              : "",
          dob: formatNavDob(member.birthDate),
        }),
      });
    },
    [navigate, ipaId],
  );

  const handleShowMore = useCallback(() => navigate("/worklist"), [navigate]);

  // A count tile → the panel DEEP-LINKED to that bucket, which is the whole
  // reason the tiles are clickable: landing on the unfiltered panel (where
  // "Show more" already goes) would make them a third copy of one
  // destination. `WorklistPage` validates `?bucket=` against the module's own
  // `BUCKET_ORDER` and seeds `initialView` from it.
  //
  // A query param rather than route state on purpose: this URL is shareable
  // and reloadable, and `/worklist` is a top-level tab the shell can also
  // arrive at from its own nav — route state would be lost on both.
  const handleSelectBucket = useCallback(
    (bucket: "urgent" | "highRisk") =>
      navigate(`/worklist?bucket=${bucket}`),
    [navigate],
  );

  // Wait for the WHOLE panel before rendering the real card — `isComplete`,
  // not `members.length > 0`.
  //
  // `usePriorityPatients` pages the panel in 100-member chunks, and bucketing
  // is portal-side: `classifyPriority` / `countByPriorityBucket` /
  // `orderUrgentTier` all rank a member RELATIVE to the rest of the panel.
  // Rendering after the first chunk therefore shows the top of a 100-member
  // slice, not the top of the panel — which is why Home listed different
  // patients than /worklist even though both read the same endpoint with the
  // same arguments. `isComplete` is deliberately distinct from `!isFetching`
  // (see its declaration in usePriorityPatients.ts): it only flips once the
  // paging loop reaches a real stopping condition, so it also tells a
  // genuinely empty panel apart from one that hasn't started.
  //
  // While `!isComplete`, show `UrgentPatientsCardSkeleton` instead of the
  // empty slot this used to leave — same reasoning, different affordance.
  // (Tradeoff: a genuinely empty panel also pages through this window, so it
  // briefly shows a skeleton before collapsing to nothing once `isComplete`
  // flips and `members.length === 0` is confirmed.)
  // A FAILED panel must be checked BEFORE the skeleton. `isComplete` is
  // `!isFetching && !error && hasSettled && …`, so `error` makes it false and
  // keeps it false — `usePriorityPatients` never clears `error` until its
  // args or `bump` change. Without this branch a single rejected panel POST
  // (a 401/403 from an expired PP group claim, a 429, a 500) pinned the
  // skeleton on Home forever, with no error, no retry and nothing in the UI
  // to say the request had already failed. Collapse to nothing instead, the
  // same way every other not-ready state here does (see the header note).
  if (isError) return null;
  if (!isComplete) return <UrgentPatientsCardSkeleton />;
  if (members.length === 0) return null;

  return (
    <UrgentPatientsCard
      members={members}
      onSelectPatient={handleSelectPatient}
      onShowMore={handleShowMore}
      onSelectBucket={handleSelectBucket}
    />
  );
}

/**
 * Panel DOBs arrive as `YYYY-MM-DD`; the nav state (and the PP crosswalk that
 * reads it) wants `MM/DD/YYYY`, which is what the module's own
 * `PatientPriorityIndexSelectedMember.dob` hands WorklistPage. Done by string
 * split rather than dayjs on purpose — parsing a date-only string through a
 * local-timezone `Date` is what shifts a wall-clock midnight back a day.
 */
function formatNavDob(birthDate?: string | null): string {
  if (!birthDate) return "";
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthDate);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  return birthDate;
}
